/**
 * Product Card Quantity Selector
 * Shows quantity controls after adding to cart
 */
(function() {
  'use strict';

  class ProductCardQuantity {
    constructor() {
      this.cartItems = {}; // Store cart items by variant ID
      this.pendingRequests = new Map(); // Track pending requests to prevent duplicates
      this.requestQueue = Promise.resolve(); // Queue for sequential requests
      this.init();
    }

    init() {
      this.bindEvents();
      this.loadCartState();
    }

    bindEvents() {
      // Bind immediately — do not wait for /cart.js (slow on iPhone).
      document.addEventListener('submit', (e) => {
        const form = e.target;
        if (form.getAttribute('data-type') !== 'add-to-cart-form') return;

        const hasQtyWrapper = !!form.querySelector('[data-qty-wrapper]');
        // Safari does not upgrade customized built-in elements (`is="product-form"`).
        // Only skip when ProductForm is actually handling the submit.
        if (!hasQtyWrapper && typeof form.handleFormSubmit === 'function') {
          return;
        }

        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        this.handleAddToCart(form, e.submitter);
      }, true);

      // Setup quantity controls
      this.setupQuantityControls();

      document.addEventListener(
        'click',
        (e) => {
          const btn = e.target.closest('[data-cart-upsell-submit].cart-upsell__btn--added');
          if (!btn || btn.classList.contains('btn--loading')) return;
          const form = btn.closest('form.cart-upsell__form');
          if (!form) return;
          e.preventDefault();
          e.stopPropagation();
          this.handleCartUpsellRemove(form);
        },
        true
      );
      
      // Listen for cart updates
      document.addEventListener('cart:updated', () => {
        this.loadCartState();
      });
      
      // Listen for cart section refresh (when drawer content is replaced)
      document.addEventListener('cart:section-refreshed', () => {
        console.log('Cart section refreshed, reloading state...');
        // Add small delay to ensure DOM is fully updated
        setTimeout(() => {
          this.loadCartState();
        }, 50);
      });
    }

    async loadCartState() {
      try {
        const response = await fetch('/cart.js', { credentials: 'same-origin' });
        if (response.ok) {
          const cart = await response.json();
          
          // Clear and rebuild cart items map
          this.cartItems = {};
          cart.items.forEach(item => {
            this.cartItems[item.variant_id] = item.quantity;
          });
          
          console.log('Cart state loaded:', this.cartItems);
          
          // Update cart count
          this.updateCartCount(cart.item_count);
          
          // Update all product cards with current cart quantities
          document.querySelectorAll('[data-type="add-to-cart-form"]').forEach(form => {
            const variantIdInput = form.querySelector('[data-variant-id]');
            if (variantIdInput) {
              const variantId = parseInt(variantIdInput.value);
              const quantity = this.cartItems[variantId] || 0;
              this.updateCardDisplay(form, quantity, false);
            }
          });

          this.syncCartUpsellButtons();
        }
      } catch (error) {
        console.error('Error loading cart state:', error);
      }
    }

    syncCartUpsellButtons() {
      document.querySelectorAll('form.cart-upsell__form').forEach((form) => {
        const variantIdInput = form.querySelector('[data-variant-id]');
        const btn = form.querySelector('[data-cart-upsell-submit]');
        if (!variantIdInput || !btn) return;

        const variantId = parseInt(variantIdInput.value, 10);
        const quantity = this.cartItems[variantId] || 0;
        const labelAdd = btn.dataset.labelAdd || '';
        const labelAdded = btn.dataset.labelAdded || labelAdd;
        const labelEl = btn.querySelector('[data-cart-upsell-label]');
        const iconPlus = btn.querySelector('.cart-upsell__icon--plus');
        const iconMinus = btn.querySelector('.cart-upsell__icon--minus');

        const ariaRemove = btn.dataset.ariaRemove || '';

        if (quantity > 0) {
          btn.type = 'button';
          btn.removeAttribute('disabled');
          btn.classList.add('cart-upsell__btn--added');
          if (labelEl) labelEl.textContent = labelAdded;
          if (iconPlus) iconPlus.classList.add('hidden');
          if (iconMinus) iconMinus.classList.remove('hidden');
          btn.setAttribute('aria-label', ariaRemove || labelAdded || labelAdd);
        } else {
          btn.type = 'submit';
          btn.removeAttribute('disabled');
          btn.classList.remove('cart-upsell__btn--added');
          if (labelEl) labelEl.textContent = labelAdd;
          if (iconPlus) iconPlus.classList.remove('hidden');
          if (iconMinus) iconMinus.classList.add('hidden');
          btn.setAttribute('aria-label', labelAdd);
        }
      });
    }

    updateCartCount(count) {
      const cartCountElements = document.querySelectorAll('cart-count, .cart-count');
      cartCountElements.forEach(element => {
        element.textContent = count;
        element.setAttribute('aria-label', `${count} items`);
        
        // Update visibility based on count
        if (count > 0) {
          element.classList.remove('hidden');
        } else {
          element.classList.add('hidden');
        }
      });
    }

    updateCardDisplay(form, quantity, animate = true) {
      const wrapper = form.querySelector('[data-qty-wrapper]');
      if (!wrapper) return;

      const addButton = wrapper.querySelector('[data-product-atc-button]');
      const qtySelector = wrapper.querySelector('[data-qty-selector]');
      const qtyInput = wrapper.querySelector('[data-qty-input]');

      if (quantity > 0) {
        // Show quantity selector with animation
        addButton.classList.add('hidden');
        qtySelector.classList.remove('hidden');
        qtySelector.style.display = '';
        qtyInput.value = quantity;
        
        // Trigger scale animation only if animate is true
        if (animate) {
          setTimeout(() => {
            qtySelector.style.animation = 'scaleIn 0.3s ease forwards';
          }, 10);
        }
      } else {
        // Show add button
        addButton.classList.remove('hidden');
        qtySelector.classList.add('hidden');
        qtySelector.style.display = 'none';
        qtyInput.value = 1;
      }
    }

    async handleCartUpsellRemove(form) {
      const variantIdInput = form.querySelector('[data-variant-id]');
      const btn = form.querySelector('[data-cart-upsell-submit]');
      if (!variantIdInput || !btn) return;

      const variantId = parseInt(variantIdInput.value, 10);
      const requestKey = `change-${variantId}`;
      if (this.pendingRequests.has(requestKey)) return;

      this.pendingRequests.set(requestKey, true);

      this.requestQueue = this.requestQueue
        .then(async () => {
          try {
            if (window.AtcButtonStates) window.AtcButtonStates.setLoading(btn);
            else {
              btn.classList.add('btn--loading');
              btn.setAttribute('aria-disabled', 'true');
            }

            let sectionsToBundle = [];
            document.documentElement.dispatchEvent(
              new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
            );

            const changeUrl =
              (window.FoxTheme && FoxTheme.routes && FoxTheme.routes.cart_change_url) || '/cart/change.js';

            const response = await this.fetchWithRetry(
              changeUrl,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Accept: 'application/json',
                  'X-Requested-With': 'XMLHttpRequest',
                },
                body: JSON.stringify({
                  id: String(variantId),
                  quantity: 0,
                  sections: sectionsToBundle,
                }),
                credentials: 'same-origin',
              },
              1
            );

            if (response.ok) {
              const rawBody = await response.text();
              let result;
              try {
                result = JSON.parse(rawBody);
              } catch (parseErr) {
                console.error('Cart change: invalid JSON', rawBody.slice(0, 400));
                return;
              }

              if (result.status || result.errors) {
                console.error('Cart change error', result.description || result.message || result);
                return;
              }

              try {
                const cartResponse = await fetch('/cart.js', { credentials: 'same-origin' });
                if (cartResponse.ok) {
                  const cartData = await cartResponse.json();
                  if (result.sections) {
                    cartData.sections = result.sections;
                  }

                  this.cartItems = {};
                  cartData.items.forEach((item) => {
                    this.cartItems[item.variant_id] = item.quantity;
                  });
                  this.updateCartCount(cartData.item_count);

                  if (window.FoxTheme && window.FoxTheme.pubsub) {
                    window.FoxTheme.pubsub.publish(window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
                      cart: cartData,
                    });
                  }
                }
              } catch (err) {
                console.error('Error fetching cart after remove:', err);
              }

              document.dispatchEvent(new CustomEvent('cart:updated'));
            } else {
              const errText = await response.text();
              console.error('Cart change failed:', response.status, errText.slice(0, 400));
            }
          } catch (error) {
            console.error('Upsell remove error:', error);
          } finally {
            if (window.AtcButtonStates) window.AtcButtonStates.reset(btn);
            else {
              btn.classList.remove('btn--loading');
              btn.removeAttribute('aria-disabled');
            }
            this.pendingRequests.delete(requestKey);
            this.syncCartUpsellButtons();
          }
        })
        .catch((error) => {
          console.error('Queue error (upsell remove):', error);
          this.pendingRequests.delete(requestKey);
        });
    }

    getAtcButtons(form, submitter) {
      if (window.AtcButtonStates) {
        return window.AtcButtonStates.fromForm(form, submitter);
      }
      return [submitter, form.querySelector('[data-product-atc-button], [type="submit"]')].filter(Boolean);
    }

    setButtonsLoading(buttons) {
      buttons.forEach((btn) => {
        if (window.AtcButtonStates) window.AtcButtonStates.setLoading(btn);
        else {
          btn.classList.add('btn--loading');
          btn.setAttribute('aria-disabled', 'true');
        }
      });
    }

    setButtonsSuccess(buttons, options) {
      buttons.forEach((btn) => {
        if (window.AtcButtonStates) window.AtcButtonStates.setSuccess(btn, options);
        else {
          btn.classList.remove('btn--loading');
          btn.classList.add('btn--success');
        }
      });
    }

    resetButtons(buttons) {
      buttons.forEach((btn) => {
        if (window.AtcButtonStates) window.AtcButtonStates.reset(btn);
        else {
          btn.classList.remove('btn--loading', 'btn--success');
          btn.removeAttribute('aria-disabled');
        }
      });
    }

    getCartAddUrl() {
      let url = (window.FoxTheme && FoxTheme.routes && FoxTheme.routes.cart_add_url) || '/cart/add';
      if (!url.includes('.js')) {
        url = url.replace(/\/?$/, '') + '.js';
      }
      return url;
    }

    async refreshCartAfterChange(parsedState) {
      try {
        const cartResponse = await fetch('/cart.js', { credentials: 'same-origin' });
        if (!cartResponse.ok) return;
        const cartData = await cartResponse.json();
        if (parsedState && parsedState.sections) {
          cartData.sections = parsedState.sections;
        }
        this.cartItems = {};
        cartData.items.forEach((item) => {
          this.cartItems[item.variant_id] = item.quantity;
        });
        this.updateCartCount(cartData.item_count);
        if (window.FoxTheme && window.FoxTheme.pubsub) {
          window.FoxTheme.pubsub.publish(window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
            cart: cartData,
          });
        }
      } catch (err) {
        console.error('Error fetching cart data:', err);
      }
    }

    async handleAddToCart(form, submitter) {
      const variantIdInput = form.elements['id'];
      if (!variantIdInput) return;
      const variantId = parseInt(variantIdInput.value, 10);
      const buttons = this.getAtcButtons(form, submitter);
      const hasQtyWrapper = !!form.querySelector('[data-qty-wrapper]');

      if (form.classList.contains('cart-upsell__form') && (this.cartItems[variantId] || 0) > 0) {
        return;
      }

      const requestKey = `add-${variantId}`;
      if (this.pendingRequests.has(requestKey)) {
        return;
      }

      const currentQty = this.cartItems[variantId] || 0;
      this.pendingRequests.set(requestKey, true);
      this.setButtonsLoading(buttons);

      this.requestQueue = this.requestQueue
        .then(async () => {
          let succeeded = false;

          try {
            const formData = new FormData(form);
            let sectionsToBundle = [];
            document.documentElement.dispatchEvent(
              new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
            );
            formData.append('sections', sectionsToBundle);
            formData.append('sections_url', window.location.pathname);

            const headers = window.FoxTheme?.utils?.fetchConfig
              ? { ...window.FoxTheme.utils.fetchConfig('javascript').headers }
              : { Accept: 'application/json' };
            delete headers['Content-Type'];
            headers['X-Requested-With'] = 'XMLHttpRequest';
            headers['Accept'] = 'application/json';

            const response = await this.fetchWithRetry(
              this.getCartAddUrl(),
              {
                method: 'POST',
                headers,
                body: formData,
                credentials: 'same-origin',
              },
              1
            );

            if (response.ok) {
              const rawBody = await response.text();
              let parsedState;
              try {
                parsedState = JSON.parse(rawBody);
              } catch (e) {
                console.error(
                  'Add to cart: response was not JSON (often missing X-Requested-With / Accept headers). Preview:',
                  rawBody.slice(0, 500)
                );
                return;
              }

              if (parsedState.status) {
                console.error('Add to cart error:', parsedState.description || parsedState.message || parsedState);
                return;
              }

              succeeded = true;
              const newQty = currentQty + 1;
              this.cartItems[variantId] = newQty;
              this.updateCartCount(Object.values(this.cartItems).reduce((sum, qty) => sum + qty, 0));
              this.setButtonsSuccess(buttons, { duration: hasQtyWrapper ? 450 : 1400 });

              if (hasQtyWrapper) {
                setTimeout(() => this.updateCardDisplay(form, newQty, true), 400);
              }

              const button = buttons[0] || form.querySelector('[data-product-atc-button]');
              if (button) {
                document.dispatchEvent(
                  new CustomEvent('product:added-to-cart', {
                    detail: { button },
                  })
                );
              }

              this.refreshCartAfterChange(parsedState).then(() => {
                document.dispatchEvent(new CustomEvent('cart:updated'));
                if (!hasQtyWrapper) {
                  const cartDrawer = document.querySelector('cart-drawer');
                  if (cartDrawer && typeof cartDrawer.show === 'function' && !cartDrawer.open) {
                    cartDrawer.show(button || submitter);
                  }
                }
              });

              const quickViewModal = form.closest('quick-view-modal');
              if (quickViewModal && quickViewModal.open) {
                quickViewModal.hide();
              }
            } else {
              console.error('Failed to add to cart, status:', response.status);
            }
          } catch (error) {
            if (error && error.name === 'AbortError') {
              console.warn('Add to cart cancelled or timed out');
            } else {
              console.error('Error adding to cart:', error);
            }
          } finally {
            if (!succeeded) this.resetButtons(buttons);
            this.pendingRequests.delete(requestKey);
            this.syncCartUpsellButtons();
          }
        })
        .catch((error) => {
          console.error('Queue error:', error);
          this.resetButtons(buttons);
          this.pendingRequests.delete(requestKey);
        });
    }

    async fetchWithRetry(url, options, maxRetries = 1) {
      for (let i = 0; i <= maxRetries; i++) {
        try {
          const response = await fetch(url, options);

          if (response.status === 429 && i < maxRetries) {
            await new Promise((resolve) => setTimeout(resolve, 800));
            continue;
          }

          return response;
        } catch (error) {
          if (error && error.name === 'AbortError') throw error;
          if (i === maxRetries) throw error;
          await new Promise((resolve) => setTimeout(resolve, 400));
        }
      }
    }

    setupQuantityControls() {
      document.addEventListener('click', async (e) => {
        // Handle plus button
        const plusButton = e.target.closest('[data-qty-plus]');
        if (plusButton) {
          e.preventDefault();
          e.stopPropagation();
          const form = plusButton.closest('form');
          await this.increaseQuantity(form);
          return;
        }

        // Handle minus button
        const minusButton = e.target.closest('[data-qty-minus]');
        if (minusButton) {
          console.log('Minus button clicked');
          e.preventDefault();
          e.stopPropagation();
          const form = minusButton.closest('form');
          await this.decreaseQuantity(form);
          return;
        }
      });
    }

    async increaseQuantity(form) {
      if (!form) {
        console.error('No form found for increaseQuantity');
        return;
      }
      
      const wrapper = form.querySelector('[data-qty-wrapper]');
      const qtyInput = wrapper.querySelector('[data-qty-input]');
      const plusButton = wrapper.querySelector('[data-qty-plus]');
      if (!plusButton || plusButton.classList.contains('btn--loading')) {
        return;
      }

      const currentQty = parseInt(qtyInput.value, 10);
      const variantIdInput = form.querySelector('[data-variant-id]');
      const variantId = parseInt(variantIdInput.value, 10);
      
      console.log('Increasing quantity:', { currentQty, variantId, newQty: currentQty + 1 });

      plusButton.classList.add('btn--loading');
      plusButton.setAttribute('aria-disabled', 'true');
      plusButton.disabled = true;
      if (window.AtcButtonStates) window.AtcButtonStates.setLoading(plusButton);

      try {
        const ok = await this.updateCartQuantity(form, variantId, currentQty + 1);
        if (ok) {
          if (window.AtcButtonStates) window.AtcButtonStates.setSuccess(plusButton, { duration: 500 });
          document.dispatchEvent(
            new CustomEvent('product:added-to-cart', {
              detail: { button: plusButton },
            })
          );
        } else if (window.AtcButtonStates) {
          window.AtcButtonStates.reset(plusButton);
        }
      } finally {
        plusButton.classList.remove('btn--loading');
        plusButton.removeAttribute('aria-disabled');
        plusButton.disabled = false;
      }
    }

    async decreaseQuantity(form) {
      if (!form) {
        console.error('No form found for decreaseQuantity');
        return;
      }
      
      const wrapper = form.querySelector('[data-qty-wrapper]');
      const qtyInput = wrapper.querySelector('[data-qty-input]');
      const currentQty = parseInt(qtyInput.value, 10);
      const variantIdInput = form.querySelector('[data-variant-id]');
      const variantId = parseInt(variantIdInput.value, 10);
      const minusButton = wrapper.querySelector('[data-qty-minus]');

      if (!minusButton || minusButton.classList.contains('btn--loading')) {
        return;
      }
      
      console.log('Decreasing quantity:', { currentQty, variantId, newQty: currentQty - 1 });
      
      if (currentQty > 0) {
        minusButton.classList.add('btn--loading');
        minusButton.setAttribute('aria-disabled', 'true');
        minusButton.disabled = true;
        try {
          document.dispatchEvent(
            new CustomEvent('product:removed-from-cart', {
              detail: { button: minusButton },
            })
          );
          await this.updateCartQuantity(form, variantId, currentQty - 1);
        } finally {
          minusButton.classList.remove('btn--loading');
          minusButton.removeAttribute('aria-disabled');
          minusButton.disabled = false;
        }
      }
    }

    async updateCartQuantity(form, variantId, quantity) {
      console.log('updateCartQuantity called:', { variantId, quantity });

      const changeUrl =
        (window.FoxTheme && FoxTheme.routes && FoxTheme.routes.cart_change_url) || '/cart/change.js';

      const run = async () => {
        try {
          const response = await this.fetchWithRetry(
            changeUrl,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
              },
              body: JSON.stringify({
                id: String(variantId),
                quantity: quantity,
              }),
              credentials: 'same-origin',
            },
            1
          );

          console.log('Cart change response:', response.status);

          if (response.ok) {
            const result = await response.json();
            console.log('Cart updated:', result);

            if (result.status || result.errors) {
              console.error('Cart change error', result.description || result.message || result);
              return false;
            }

            if (quantity > 0) {
              this.cartItems[variantId] = quantity;
            } else {
              delete this.cartItems[variantId];
            }

            const totalItems = Object.values(this.cartItems).reduce((sum, qty) => sum + qty, 0);
            this.updateCartCount(totalItems);

            this.updateCardDisplay(form, quantity, false);

            try {
              const cartResponse = await fetch('/cart.js', { credentials: 'same-origin' });
              if (cartResponse.ok) {
                const cartData = await cartResponse.json();

                if (window.FoxTheme && window.FoxTheme.pubsub) {
                  window.FoxTheme.pubsub.publish(window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
                    cart: cartData,
                  });
                }
              }
            } catch (err) {
              console.error('Error fetching cart data:', err);
            }

            document.dispatchEvent(new CustomEvent('cart:updated'));
            return true;
          }

          const errorText = await response.text();
          console.error('Failed to update cart:', response.status, errorText);
          if (response.status === 429) {
            console.warn('Rate limit exceeded. Please wait before trying again.');
          }
          return false;
        } catch (error) {
          console.error('Error updating cart:', error);
          return false;
        }
      };

      const thisOp = this.requestQueue.then(run);
      this.requestQueue = thisOp.catch((err) => {
        console.error('Queue error:', err);
      });
      return thisOp;
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      new ProductCardQuantity();
    });
  } else {
    new ProductCardQuantity();
  }
})();
